import { ActionIcon, Avatar, Group, Input, Text, Title, Tooltip } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useFocusTrap } from '@mantine/hooks';
import { IconRefresh, IconX } from '@tabler/icons-react';
import { Dispatch, FormEvent, SetStateAction, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatContent, I_SignatureMessages, Loading, ChatScroll, SvgSendStop, getReferencesService } from '@mygpt/app';
import { ReferenceSign } from '@mygpt/types';
import { CHATBOT_AVATAR_ARRAY } from '@/components/Widget/Embed/Form/ChatbotAvatar';
import { I_IconSetting } from '@/components/Widget/Embed/form-context';
import { SvgSend } from '@/pages/ChatWidget/icon';
import { SessionListItem, SessionListParam } from '@/types/interface';

import classes from './chatView.module.css';

export interface I_configs {
  signatureMessages?: I_SignatureMessages;
  suggestedMessages?: string[];
  inputPlaceholder?: string;
  footerBrand?: string;
  answerSource?: {
    checked: boolean;
  };
  widgetColor?: {
    chatColor?: string;
    sendButtonColor?: string;
    userMessageColor?: string;
    botMessageColor?: string;
  };
  chatbotAvatar: I_IconSetting;
}

interface Props {
  title: string;
  onRefresh: () => void;
  onClose: () => void;
  loading: boolean;
  sessionList: SessionListItem;
  historySessionList?: SessionListItem;
  submit: (values: { value: string }, event?: FormEvent<HTMLFormElement>) => void;
  form: UseFormReturnType<{ value: string }>;

  configs?: I_configs;
  sessionInfo?: {
    aiId: string;
    sessionId: string;
  };
  updateSessionList?: (state: SessionListItem) => void;
  setHistorySessionList: Dispatch<SetStateAction<SessionListItem>>;
  fetchQuestion: (value: string, fqaId?: string) => void;
  stopAnswer: () => void;
}

export default (props: Props) => {
  const { t } = useTranslation();

  const {
    configs,
    fetchQuestion,
    form,
    historySessionList,
    loading,
    onClose,
    onRefresh,
    sessionInfo,
    sessionList,
    setHistorySessionList,
    stopAnswer,
    submit,
    title,
  } = props;
  const {
    answerSource: configsAnswerSource,
    chatbotAvatar,
    footerBrand,
    inputPlaceholder,
    signatureMessages,
    suggestedMessages,
    widgetColor,
  } = configs ?? {};
  const { botMessageColor, chatColor, sendButtonColor, userMessageColor } = widgetColor ?? {};

  const focusTrapRef = useFocusTrap();

  const { messages, pending } = sessionList;

  const chatMessages = useMemo(() => {
    const historySessionListMessage = historySessionList ? historySessionList.messages : [];
    return [
      ...historySessionListMessage,
      ...messages,
      ...(pending
        ? [
            {
              isAnswer: true,
              text: pending,
            },
          ]
        : []),
    ];
  }, [sessionList, historySessionList]);

  const answerSource = useMemo(() => {
    if (!configsAnswerSource) return true;
    return configsAnswerSource?.checked;
  }, [configsAnswerSource]);

  const isLastMessage = (messageId: string) => {
    if (messages.length) {
      return messages.at(-1)?.messageId === messageId;
    } else if (historySessionList?.messages.length) {
      return historySessionList.messages.at(-1)?.messageId === messageId;
    } else {
      return false;
    }
  };

  const handleMessages = ({ messageId, rating }: { rating: number; messageId: string }) => {
    if (!historySessionList) return;
    const hisroryList = historySessionList.messages;
    const historyIndex = hisroryList.findIndex((item: SessionListParam) => item.messageId === messageId);
    hisroryList[historyIndex].rating = rating;
    setHistorySessionList({ messages: hisroryList });
  };

  const getReference = (ai_id: string, message_id: string) => {
    return new Promise<ReferenceSign[]>((resolve) => {
      getReferencesService(ai_id, message_id).then((items) => {
        setHistorySessionList((list) => {
          const messages = list.messages.map((item) => {
            if (item.messageId === message_id) {
              item.reference_indexes = items.data;
            }
            return item;
          });
          return { ...list, messages };
        });
        resolve(items.data);
      });
    });
  };

  const ChatbotAvatar = () => {
    const CustomAvatar = CHATBOT_AVATAR_ARRAY[chatbotAvatar?.index || 0];

    return (
      <Avatar size="sm" radius="xl" src={chatbotAvatar?.type === 'custom' ? chatbotAvatar.url : ''}>
        <span className={`${classes.avatar} ${classes.robotAvatar}`}>
          {/* <IconMessageChatbot size={20} /> */}
          <span className="scale-75">
            <CustomAvatar />
          </span>
        </span>
      </Avatar>
    );
  };

  return (
    <div className={`h-full flex flex-col`}>
      <section
        className={`${classes.header} flex-none p-5`}
        style={{ background: chatColor ? chatColor : 'linear-gradient(90.07deg, #AF71FF 4.16%, #7C68FB 134.24%)' }}
      >
        <Group justify="space-between" wrap="nowrap">
          <Title style={{ wordBreak: 'break-word' }} order={5} fw={500} size={16}>
            {title}
          </Title>
          <Group wrap="nowrap">
            <Tooltip label={t('completions.reset_chat')}>
              <ActionIcon className={classes.headerIcon} onClick={onRefresh}>
                <IconRefresh size={18} />
              </ActionIcon>
            </Tooltip>
            <ActionIcon className={classes.headerIcon} onClick={onClose}>
              <IconX size={18} />
            </ActionIcon>
          </Group>
        </Group>
      </section>
      <ChatScroll loading={loading}>
        <section className={`${classes.answerWrap} flex-grow overflow-y-auto`}>
          {chatMessages.map((item, index) => (
            <div
              key={index}
              className={`${classes.sessionItem} ${
                item.isAnswer ? classes.answerText : classes.questionText
              } flex gap-3 break-all`}
            >
              {item.isAnswer && (
                <span>
                  <ChatbotAvatar />
                </span>
              )}
              <div
                className={!item.isAnswer ? classes.questionWrap : ''}
                style={{
                  backgroundColor: !item.isAnswer
                    ? userMessageColor
                      ? userMessageColor
                      : '#7C68FB'
                    : botMessageColor
                    ? botMessageColor
                    : '#F9FAFB',
                }}
              >
                <ChatContent
                  id={sessionInfo?.aiId || ''}
                  item={item}
                  index={index}
                  answerSource={answerSource}
                  pending={pending}
                  chatMessages={chatMessages}
                  sessionId={sessionInfo?.sessionId || ''}
                  signatureMessages={signatureMessages}
                  fetchQuestion={fetchQuestion}
                  isLastMessage={isLastMessage}
                  regenerate={fetchQuestion}
                  handleMessages={handleMessages}
                  getReference={getReference}
                />
              </div>
            </div>
          ))}
          {loading && !pending && (
            <div className={`${classes.sessionItem} ${classes.answerText} flex items-start gap-3 break-all`}>
              <span>
                <ChatbotAvatar />
              </span>
              <div className="flex items-center whitespace-pre-wrap">
                <Loading />
              </div>
            </div>
          )}
        </section>
      </ChatScroll>
      {suggestedMessages && (
        <section className="flex-none px-5 pt-2">
          <Group gap={10}>
            {suggestedMessages.map((item, index) => (
              <span
                key={index}
                className="bg-gray-200 rounded-md p-2 cursor-pointer text-sm"
                onClick={() => {
                  submit({ value: item });
                }}
              >
                {item}
              </span>
            ))}
          </Group>
        </section>
      )}
      <section className={`${classes.inputWrap} flex-none px-5`}>
        <form onSubmit={form.onSubmit(submit)}>
          <Input
            ref={focusTrapRef}
            size="md"
            className={classes.input}
            placeholder={inputPlaceholder ? inputPlaceholder : 'Ask a question about this Website...'}
            rightSection={
              loading ? (
                <ActionIcon radius="xl" variant="transparent" onClick={stopAnswer}>
                  <SvgSendStop />
                </ActionIcon>
              ) : (
                <ActionIcon
                  mr={10}
                  style={{ color: sendButtonColor ? sendButtonColor : '' }}
                  type="submit"
                  radius="xl"
                  size="lg"
                  variant="subtle"
                >
                  <SvgSend />
                </ActionIcon>
              )
            }
            {...form.getInputProps('value')}
          />
        </form>
        <section className="text-gray-400 min-h-[18px]">
          <Text ta="center" size="xs" mt={5} mb={5}>
            {footerBrand ? (
              footerBrand
            ) : (
              <>
                Powered by{' '}
                <a href="https://gptbase.ai/" target="_blank" rel="noreferrer" className="text-blue-300 underline">
                  GBase
                </a>
              </>
            )}
          </Text>
        </section>
      </section>
    </div>
  );
};
